// src/pages/ImageDetailPage.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Card, CardMedia, Divider, Chip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress, // Import CircularProgress
  Alert // Import Alert
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Make sure ALL needed functions are imported from api/images.js
import { getImageDetails, submitImage, getImageFile, submitBatchImagesToSyndigo } from '../api/images';
import SubmitWithBadgeDialog from '../components/SubmitWithBadgeDialog';
import ImageVersionHistory from '../components/ImageVersionHistory';
import { scheduleSubmission, getScheduledSubmissionsForImage } from '../api/scheduler';
import apiClient from '../api/client';
import { format } from 'date-fns';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';

const ImageDetailPage = () => {
  const { id: imageId } = useParams(); // Get ID from URL, rename for clarity
  const navigate = useNavigate();

  // State for metadata fetch
  const [imageMetadata, setImageMetadata] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState(null);

  // State specifically for loading and displaying the image blob
  const [imageUrl, setImageUrl] = useState(null); // Will hold the blob: URL
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);

  // State for dialogs/actions
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]); // For SubmitWithBadgeDialog

  // State for scheduled submissions
  const [scheduledSubmissions, setScheduledSubmissions] = useState([]);

  // --- Effect 1: Fetch Image Metadata ---
  useEffect(() => {
    let isMounted = true; // Track if component is mounted
    const fetchImageMetadata = async () => {
      if (!imageId) {
          if (isMounted) {
               setMetaError("No Image ID provided in URL.");
               setMetaLoading(false);
          }
          return;
      }
      console.log(`DETAIL PAGE: useEffect[${imageId}] - Fetching METADATA.`);
      if (isMounted) {
          setMetaLoading(true);
          setMetaError(null);
          setImageMetadata(null); // Clear old data
          // Clear previous image display state when metadata reloads
          if (imageUrl && imageUrl.startsWith('blob:')) URL.revokeObjectURL(imageUrl);
          setImageUrl(null);
          setImageError(null);
          setImageLoading(false);
      }

      try {
        const data = await getImageDetails(imageId);
        console.log("DETAIL PAGE: Metadata fetched:", data);
         // Check if component is still mounted before setting state
        if (isMounted) {
             if (data) {
                 setImageMetadata(data);
             } else {
                 throw new Error("Image metadata not found.");
             }
        }
      } catch (err) {
        console.error('Error fetching image details:', err);
         if (isMounted) setMetaError(err.response?.data?.detail || err.message || 'Failed to load image details');
      } finally {
         if (isMounted) setMetaLoading(false);
      }
    };

    fetchImageMetadata();

    // Cleanup function for metadata fetch effect
    return () => {
       isMounted = false;
    }
  }, [imageId]); // Re-fetch metadata ONLY if imageId changes

  // --- Effect 2: Fetch Image Blob ---
  // Runs whenever imageMetadata state changes (and meets conditions)
  useEffect(() => {
    let isMounted = true; // Track mount status for this effect

    const fetchImageBlob = async () => {
      // Guard conditions: Only fetch if metadata is loaded, status is success or submitted, and we have an ID
      if (!imageMetadata || (!['success', 'submitted'].includes(imageMetadata.status)) || !imageMetadata.id) {
        console.log("DETAIL PAGE: Conditions not met for fetching image blob.", imageMetadata);
        // Ensure previous blob URL is cleared if status is no longer 'success' or 'submitted'
        if (imageUrl && imageUrl.startsWith('blob:')) {
            URL.revokeObjectURL(imageUrl);
        }
        if(isMounted) setImageUrl(null); // Set to null if conditions aren't met
        return;
      }

      const currentImageId = imageMetadata.id; // Capture ID for async safety
      console.log(`DETAIL PAGE: useEffect[imageMetadata] - Attempting to fetch BLOB for ID: ${currentImageId}`);
      if(isMounted) {
          setImageLoading(true);
          setImageError(null);
           // Revoke previous blob URL *before* fetching new one
          if (imageUrl && imageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(imageUrl);
          }
           setImageUrl(null); // Clear current URL before fetching
      }

      try {
        const blob = await getImageFile(currentImageId); // Authenticated request
        console.log(`DETAIL PAGE: Blob received for ID ${currentImageId}:`, blob);

        if (isMounted) { // Check mount status *before* setting state
            if (blob && blob instanceof Blob && blob.size > 0) {
                const newImageUrl = URL.createObjectURL(blob);
                console.log(`DETAIL PAGE: Created new object URL for ID ${currentImageId}:`, newImageUrl);
                setImageUrl(newImageUrl); // Set the new blob URL
            } else {
                console.error(`getImageFile did not return a valid blob for ID ${currentImageId}:`, blob);
                throw new Error("Failed to load image data (invalid blob received).");
            }
        }
      } catch (err) {
        console.error(`Error fetching image file blob for ID ${currentImageId}:`, err);
        if (isMounted) {
            setImageError(err.response?.data?.detail || err.message || 'Failed to load image file');
            setImageUrl(null); // Clear URL on error
        }
      } finally {
        if (isMounted) setImageLoading(false);
      }
    };

    fetchImageBlob();

    // Cleanup function for *this specific effect instance*
    return () => {
      isMounted = false;
      console.log("DETAIL PAGE: Blob fetch effect cleanup.");
    };
    // Depend on the metadata object. When it re-fetches after an edit,
    // this effect will re-run and fetch the new blob.
  }, [imageMetadata]); // Re-run when metadata changes

   // --- Effect 3: Cleanup Blob URL ---
   // Separate effect specifically for cleaning up the blob URL when the component unmounts
   // OR when the imageUrl state itself is changed to null/something else.
   useEffect(() => {
     const urlToClean = imageUrl; // Capture the URL value when the effect runs
     return () => {
       if (urlToClean && urlToClean.startsWith('blob:')) {
         console.log("DETAIL PAGE: Revoking Object URL on unmount/change:", urlToClean);
         URL.revokeObjectURL(urlToClean);
       }
     };
   }, [imageUrl]); // Run cleanup when imageUrl state changes or on unmount

   // --- Effect 4: Fetch Scheduled Submissions ---
   useEffect(() => {
     const fetchScheduledSubmissions = async () => {
       if (!imageMetadata?.id) return;
       try {
         const submissions = await getScheduledSubmissionsForImage(imageMetadata.id);
         setScheduledSubmissions(submissions || []);
       } catch (err) {
         console.error('Error fetching scheduled submissions:', err);
         setScheduledSubmissions([]);
       }
     };
     fetchScheduledSubmissions();
   }, [imageMetadata?.id]);


  // --- Download Image Handler (Uses authenticated fetch) ---
  const handleDownload = async () => {
    if (!imageMetadata || (!['success', 'submitted'].includes(imageMetadata.status))) return;
    setImageError(null); // Clear previous errors
    // Consider adding a loading state for download button if it takes time
    try {
      console.log(`Downloading image file for ID: ${imageMetadata.id}`);
      const blob = await getImageFile(imageMetadata.id); // Authenticated request
      if (!blob || !(blob instanceof Blob) || blob.size === 0) {
        throw new Error("Failed to download image data.");
      }
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      const blobUrl = URL.createObjectURL(blob); // Create temporary URL for download
      link.href = blobUrl;
      link.download = `MRHI_${imageMetadata.gtin}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl); // Clean up download URL immediately
      console.log("Download triggered.");
    } catch(err) {
      console.error("Download failed:", err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to download image';
      setImageError(errorMessage);
      // Show feedback to user about download error
      alert(`Download failed: ${errorMessage}`);
    }
  };

  // --- Edit Image Handler (Navigation) ---
  const handleEdit = () => {
    // Navigate to the edit page using the ID from useParams
    navigate(`/images/${imageId}/edit`);
  };

  // --- Open Submit Dialog Handler ---
  const handleOpenSubmitDialog = () => {
    if (!imageMetadata) return;
    // Set selected images for the dialog
    setSelectedImages([imageMetadata]);
    setSubmitDialogOpen(true);
  };

  // --- Submit Handler for SubmitWithBadgeDialog ---
  const handleSubmitToSyndigo = async (scheduledDate = null, options = {}) => {
    const publishAfterSubmit = !!options.publishAfterSubmit;
    const publishOnSubmit = options.publishOnSubmit !== undefined ? !!options.publishOnSubmit : true;
    setSubmitting(true);
    setMetaError(null);
    try {
      // Get the image ID from state
      const imageId = imageMetadata.id;

      // Handle different image statuses
      if (imageMetadata.status === 'failed_submission') {
        // For failed submissions, use retry endpoint first
        const { retrySubmitImage } = await import('../api/images');
        const updatedImage = await retrySubmitImage(imageId, { publishAfterSubmit });
        setImageMetadata(updatedImage);
        // If retry was successful, we're done
        if (updatedImage.status === 'submitted') {
          setSubmitDialogOpen(false);
          return;
        }
      } else if (!imageMetadata.is_staged && imageMetadata.status === 'success') {
        // For successful images that aren't staged, stage them first
        const { stageBatchImages } = await import('../api/images');
        await stageBatchImages({ image_ids: [imageId] });
        // Update local state to reflect staging
        setImageMetadata(prev => ({ ...prev, is_staged: true }));
      }

      // If scheduledDate is provided, schedule the submission
      if (scheduledDate) {
        // Schedule the submission for later
        // Convert the string date to a Date object for the API
        const scheduledDateObj = new Date(scheduledDate);
        const response = await scheduleSubmission(imageId, {
          scheduledFor: scheduledDateObj,
          createBadgeRemovalSuggestions: false,
          publishOnSubmit,
        });
        // For scheduled submissions, we can attach badge removals to the parent submission
        // This will be handled by handleScheduleBadgeRemovals with the submission ID
      } else {
        // For immediate submission, badge removals must be scheduled BEFORE submission
        // The SubmitWithBadgeDialog will handle this coordination

        // Submit immediately using batch submit (same as Review & Submit page)
        await submitBatchImagesToSyndigo({
          image_ids: [imageId],
          publish_after_submit: publishAfterSubmit,
        });
      }
      
      // Refresh metadata
      const updatedMetadata = await getImageDetails(imageId);
      setImageMetadata(updatedMetadata);
      setSubmitDialogOpen(false);
      
      // Show success message
      setMetaError(null);
    } catch (err) {
      console.error('Error submitting image:', err);
      setMetaError(err.response?.data?.detail || err.message || 'Failed to submit image');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Badge Removal Handler ---
  const handleScheduleBadgeRemovals = async (badgeRemovals) => {
    try {
      console.log('Scheduling badge removals:', badgeRemovals);
      
      // Handle badge removal scheduling inline (same as Review & Submit page)
      for (const removal of badgeRemovals) {
        try {
          // Schedule the badge removal
          const removalDate = removal.removalDate || removal.suggestedRemovalDate;
          
          // Create a scheduled submission for badge removal
          // Use the full endpoint path like SearchPage does
          const response = await apiClient.post(`/scheduler/schedule/${removal.imageId}`, {
            scheduled_for: removalDate,
            is_badge_removal: true,
            badge_type: removal.badge
          });
          console.log(`Badge removal scheduled for image ${removal.imageId}:`, response.data);
        } catch (err) {
          console.error(`Error scheduling badge removal for image ${removal.imageId}:`, err);
          // Check if the error is because the image isn't staged
          if (err.response?.status === 400 && err.response?.data?.detail?.includes('not staged')) {
            console.error('Cannot schedule badge removal - image is not staged');
          }
        }
      }
      
      console.log(`${badgeRemovals.length} badge removal(s) scheduled.`);
    } catch (err) {
      console.error('Error scheduling badge removals:', err);
      // Don't set error for badge scheduling failures
    }
  };

  // --- Render Logic ---
  // Handle initial metadata loading state
  if (metaLoading) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
  }
  // Handle critical metadata error state (if metadata never loaded)
  if (metaError && !imageMetadata) {
      return <Alert severity="error">Error loading image details: {metaError}</Alert>;
  }
  // Handle case where metadata loaded but was unexpectedly null/empty
  if (!imageMetadata) {
      // Added check to avoid rendering null before first load finishes
      if (!metaLoading) {
         return <Typography>Image details not found.</Typography>;
      }
      // Otherwise, still loading, return null or loading indicator
      return null;
  }

  // If metadata loaded successfully, return the main page content
  return (
    <Box> {/* Main content Box */}
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">MRHI Details</Typography>
        <Box>
          {imageMetadata.status === 'success' && !imageMetadata.is_submitted && (
            <>
              <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit} sx={{ mr: 1 }}>Edit</Button>
              <Button variant="contained" color="primary" startIcon={<PublishIcon />} onClick={handleOpenSubmitDialog} sx={{ mr: 1 }}>Submit</Button>
            </>
          )}
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={!['success', 'submitted'].includes(imageMetadata.status)}>Download</Button>
        </Box>
      </Box>

      {/* Display non-critical errors if they occurred */}
      {metaError && <Alert severity="warning" sx={{ mb: 2 }}>Could not refresh metadata: {metaError}</Alert>}
      {imageError && <Alert severity="error" sx={{ mb: 2 }}>Error loading image preview: {imageError}</Alert>}

      <Grid container spacing={3}>
        {/* Image preview Grid item */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3, minHeight: 400, display:'flex', justifyContent:'center', alignItems:'center' }}>
            {/* Show loading indicator while image blob is fetching */}
            {imageLoading && <CircularProgress /> }

            {/* Show image only if NOT loading, NO error, status is success or submitted, and blob URL exists */}
            {!imageLoading && !imageError && ['success', 'submitted'].includes(imageMetadata.status) && imageUrl ? (
              <CardMedia
                component="img"
                image={imageUrl} // Use the blob URL state
                alt={`MRHI for GTIN ${imageMetadata.gtin}`}
                sx={{ width: '100%', maxHeight: '600px', objectFit: 'contain' }}
                onError={(e) => { console.error('CardMedia failed to load blob URL:', imageUrl, e); setImageError("Browser could not display image from blob URL.") }}
              />
            ) : (
               // Otherwise (if not loading image), show status message
               !imageLoading && (
                   <Typography color={imageMetadata.status === 'failed' ? 'error' : 'textSecondary'} sx={{p: 3}}>
                    {imageMetadata.status === 'failed' ? `Generation failed: ${imageMetadata.reason || 'Unknown reason'}` :
                     imageMetadata.status === 'processing' ? 'Image processing...' :
                     imageMetadata.status === 'pending' ? 'Image pending...' :
                     ['success', 'submitted'].includes(imageMetadata.status) && !imageUrl ? 'Image preview unavailable.' : // Status is success/submitted but blob failed/not loaded yet
                     'Image status unknown or preview unavailable.' // Default for other statuses
                     }
                   </Typography>
               )
            )}
          </Card>
        </Grid>

        {/* Details Column Grid item */}
        <Grid item xs={12} md={6}>
          {/* Information Paper */}
          <Paper sx={{ p: 3, mb: 3 }}>
             <Typography variant="h6" gutterBottom> Image Information </Typography>
             <Grid container spacing={1}> {/* Reduced spacing */}
                <Grid item xs={4}><Typography variant="body2" color="textSecondary">GTIN</Typography></Grid>
                <Grid item xs={8}><Typography variant="body1" sx={{wordBreak: 'break-all'}}>{imageMetadata.gtin}</Typography></Grid>

                <Grid item xs={4}><Typography variant="body2" color="textSecondary">Status</Typography></Grid>
                <Grid item xs={8}><Chip label={imageMetadata.status} color={ imageMetadata.status === 'success' ? 'success' : imageMetadata.status === 'failed' ? 'error' : 'default' } size="small" /></Grid>

                <Grid item xs={4}><Typography variant="body2" color="textSecondary">Created</Typography></Grid>
                <Grid item xs={8}><Typography variant="body1">{new Date(imageMetadata.created_at).toLocaleString()}</Typography></Grid>

                 <Grid item xs={4}><Typography variant="body2" color="textSecondary">Last Updated</Typography></Grid>
                 <Grid item xs={8}><Typography variant="body1">{new Date(imageMetadata.updated_at).toLocaleString()}</Typography></Grid>

                 <Grid item xs={4}><Typography variant="body2" color="textSecondary">Submitted</Typography></Grid>
                 <Grid item xs={8}>{imageMetadata.is_submitted ? <Typography variant="body1">{new Date(imageMetadata.submitted_at).toLocaleString()}</Typography> : <Chip label="Not Submitted" variant="outlined" size="small" />}</Grid>
             </Grid>
          </Paper>

          {/* Product details */}
          {/* Ensure product_data exists and is an object before trying to access properties */}
          {imageMetadata.product_data && typeof imageMetadata.product_data === 'object' ? (
            <Paper sx={{ p: 3 }}>
               <Typography variant="h6" gutterBottom> Product Details </Typography>
               <Grid container spacing={1}>
                    <Grid item xs={4}><Typography variant="body2" color="textSecondary">Brand</Typography></Grid>
                    <Grid item xs={8}><Typography variant="body1">{imageMetadata.product_data.brand || 'N/A'}</Typography></Grid>

                    <Grid item xs={4}><Typography variant="body2" color="textSecondary">Flavor</Typography></Grid>
                    <Grid item xs={8}><Typography variant="body1">{imageMetadata.product_data.flavor || 'N/A'}</Typography></Grid>

                     <Grid item xs={4}><Typography variant="body2" color="textSecondary">Size</Typography></Grid>
                     <Grid item xs={8}><Typography variant="body1">{imageMetadata.product_data.size || 'N/A'} {imageMetadata.product_data.size_uom || ''}</Typography></Grid>

                     <Grid item xs={4}><Typography variant="body2" color="textSecondary">Layout</Typography></Grid>
                     <Grid item xs={8}><Typography variant="body1">{imageMetadata.product_data.layout || 'N/A'}</Typography></Grid>

                    {imageMetadata.product_data.claims && imageMetadata.product_data.claims.length > 0 && (
                      <>
                        <Grid item xs={4}><Typography variant="body2" color="textSecondary">Claims</Typography></Grid>
                        <Grid item xs={8}><Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{imageMetadata.product_data.claims.map((claim, index) => (<Chip key={index} label={claim} size="small" />))}</Box></Grid>
                      </>
                    )}
                     {imageMetadata.status === 'failed' && imageMetadata.reason && (
                        <>
                            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                            <Grid item xs={4}><Typography variant="body2" color="textSecondary">Error Reason</Typography></Grid>
                            <Grid item xs={8}><Typography variant="body1" color="error">{imageMetadata.reason}</Typography></Grid>
                        </>
                     )}
               </Grid>
            </Paper>
          ) : (
             // Optionally show something if product_data is missing or not an object
             <Paper sx={{ p: 3 }}><Typography variant="body2" color="textSecondary">Product details not available.</Typography></Paper>
          )}

          {/* Scheduled Submissions Section */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon /> Scheduled Submissions
            </Typography>
            {scheduledSubmissions.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {scheduledSubmissions.map((submission) => (
                  <Box
                    key={submission.id}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: submission.status === 'pending' ? 'action.hover' : 'transparent'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {submission.is_badge_removal && <LocalOfferIcon fontSize="small" color="secondary" />}
                        <Typography variant="subtitle2">
                          {submission.is_badge_removal
                            ? `Badge Removal: ${submission.original_badges?.join(', ') || 'Unknown'}`
                            : 'Submission'}
                        </Typography>
                      </Box>
                      <Chip
                        label={submission.status}
                        size="small"
                        color={
                          submission.status === 'pending' ? 'default' :
                          submission.status === 'completed' ? 'success' :
                          submission.status === 'failed' ? 'error' : 'default'
                        }
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Scheduled for: {format(new Date(submission.scheduled_for), 'MMM d, yyyy h:mm a')}
                    </Typography>
                    {submission.user_email && (
                      <Typography variant="body2" color="textSecondary">
                        Scheduled by: {submission.user_email}
                      </Typography>
                    )}
                    {submission.submitted_at && (
                      <Typography variant="body2" color="textSecondary">
                        Submitted: {format(new Date(submission.submitted_at), 'MMM d, yyyy h:mm a')}
                      </Typography>
                    )}
                    {submission.error_message && (
                      <Typography variant="body2" color="error">
                        Error: {submission.error_message}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No scheduled submissions for this image.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Version History */}
      {imageMetadata && (
        <ImageVersionHistory 
          imageId={imageMetadata.id} 
          currentVersionId={imageMetadata.current_version_id}
        />
      )}

      {/* Submit with Badge Dialog - same as Review & Submit page */}
      <SubmitWithBadgeDialog
        open={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        selectedImages={selectedImages}
        onSubmit={handleSubmitToSyndigo}
        onScheduleBadgeRemovals={handleScheduleBadgeRemovals}
        isSubmitting={submitting}
      />
    </Box> // End Main content Box
  ); // End main return
}; // End component function

export default ImageDetailPage;